import * as XLSX from 'xlsx';
import Url from '../global/Url';
import { getCsrf } from './auth';

// Columnas que debe traer el archivo, en este orden (igual que el sistema viejo).
export const COLUMNAS = ['IDENTIFICACION', 'NOMBRE', 'CELULAR', 'CIUDAD', 'TARIFA', 'OTROS', 'OTROS1'];

const FILAS_POR_LOTE = 2000;

/** Campañas activas creadas en el año en curso, para etiquetar la base al subir. */
export async function getCampaniasActivas(destino = 'movistar') {
    const resp = await fetch(Url.Api + 'campaniasActivas?destino=' + encodeURIComponent(destino), { credentials: 'same-origin' });
    const data = await resp.json();
    if (!resp.ok || data.header !== 'OK') throw new Error(data.message || 'No se pudieron cargar las campañas');
    return data.campanias || [];
}

/** Lee un archivo Excel/CSV y devuelve { headers, filas } como objetos. */
export function leerArchivo(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const wb = XLSX.read(evt.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const matriz = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
                if (matriz.length === 0) { reject(new Error('El archivo está vacío')); return; }

                const headers = matriz[0].map((h) => String(h).trim().toUpperCase());
                const filas = [];
                for (let i = 1; i < matriz.length; i++) {
                    const fila = matriz[i];
                    if (!fila || fila.every((c) => String(c).trim() === '')) continue; // saltar vacías
                    const obj = {};
                    headers.forEach((h, j) => { obj[h] = String(fila[j] ?? '').trim(); });
                    filas.push(obj);
                }
                resolve({ headers, filas });
            } catch (e) {
                reject(new Error('No se pudo leer el archivo: ' + e.message));
            }
        };
        reader.onerror = () => reject(new Error('Error al leer el archivo'));
        reader.readAsBinaryString(file);
    });
}

/** Valida que las columnas del archivo sean exactamente las esperadas. */
export function validarColumnas(headers) {
    if (headers.length !== COLUMNAS.length) {
        return `El archivo tiene ${headers.length} columnas y se esperan ${COLUMNAS.length}.`;
    }
    for (let i = 0; i < COLUMNAS.length; i++) {
        if (headers[i] !== COLUMNAS[i]) {
            return `Columna ${i + 1}: se encontró "${headers[i]}", se esperaba "${COLUMNAS[i]}".`;
        }
    }
    return null;
}


/**
 * Valida una fila. Devuelve el motivo del error (texto) o null si está bien.
 * Extensible: aquí se agregan más reglas si hicieran falta.
 */
export function validarFila(f) {
    const tel = String(f.CELULAR ?? '').replace(/\D/g, '');
    if (tel === '') return 'Celular vacío';
    if (!/^09[0-9]{8}$/.test(tel)) return 'Celular debe ser 09XXXXXXXX (10 dígitos)';
    // El nombre puede venir vacío (se registra igual); lo único obligatorio es el celular.
    return null;
}

/** Cuenta cuántas filas fallan (para avisar antes de subir). */
export function contarInvalidos(filas) {
    let malos = 0;
    for (const f of filas) if (validarFila(f) !== null) malos++;
    return malos;
}

/**
 * Genera y descarga un Excel con SOLO las filas que fallaron, más una columna
 * MOTIVO. Mantiene las columnas originales para corregir y volver a subir.
 */
export function descargarFallos(fallos) {
    const encabezado = [...COLUMNAS, 'MOTIVO'];
    const matriz = [encabezado];
    for (const { fila, motivo } of fallos) {
        matriz.push([...COLUMNAS.map((c) => fila[c] ?? ''), motivo]);
    }
    const ws = XLSX.utils.aoa_to_sheet(matriz);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fallos');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    XLSX.writeFile(wb, `fallos-carga-${stamp}.xlsx`);
}

/**
 * Sube las filas por lotes al servidor, que hace UPSERT (actualiza si el número
 * ya existe, inserta si no). Llama onProgreso(acumulado) tras cada lote.
 */
export async function subirLeads(filas, onProgreso, destino = 'movistar', campania = '') {
    const stamp = new Date();
    // Si el usuario eligió una campaña activa, la base se etiqueta con ella;
    // si no, se usa el nombre automático por fecha.
    const dbName = (campania && campania.trim() !== '')
        ? campania.trim()
        : `database-${stamp.getDate()}-${stamp.getMonth() + 1}-${stamp.getFullYear()}`;

    // Separar válidas de fallidas ANTES de enviar: solo las válidas van al servidor,
    // y las fallidas se guardan con su motivo para el archivo de errores descargable.
    const validas = [];
    const fallos = [];
    for (const f of filas) {
        const motivo = validarFila(f);
        if (motivo) fallos.push({ fila: f, motivo });
        else validas.push(f);
    }

    const total = {
        insertadas: 0, actualizadas: 0, invalidas: fallos.length, procesadas: 0,
        dbName, totalFilas: filas.length, fallos,
    };

    let primero = true;
    for (let inicio = 0; inicio < validas.length; inicio += FILAS_POR_LOTE) {
        const lote = validas.slice(inicio, inicio + FILAS_POR_LOTE).map((f) => ({
            identificacion: f.IDENTIFICACION, nombre: f.NOMBRE, celular: f.CELULAR,
            ciudad: f.CIUDAD, tarifa: f.TARIFA, otros: f.OTROS, otros1: f.OTROS1,
        }));

        const resp = await fetch(Url.Api + 'cargaLeads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF': getCsrf() || '' },
            body: JSON.stringify({ destino, database_name: dbName, primero, rows: lote }),
        });
        const data = await resp.json();
        if (!resp.ok || data.header !== 'OK') {
            throw new Error(data.message || 'Error del servidor en un lote');
        }

        total.insertadas += data.insertadas;
        total.actualizadas += data.actualizadas;
        total.procesadas += lote.length + (inicio === 0 ? 0 : 0);
        primero = false;
        if (onProgreso) onProgreso({ ...total, procesadas: total.procesadas + fallos.length });
    }
    total.procesadas = total.totalFilas;
    if (onProgreso) onProgreso({ ...total });
    return total;
}
